import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  Country,
  Nullable,
  OrganizerEmployee,
  RankingRegion,
  Store,
  User,
  UserWithToken,
  WithData,
  WithMeta,
} from '@ygoargentina/sdk/api/api.types';
import z from 'zod';
import axiosClient from './request-client';
import { AxiosRequestConfig } from 'axios';
import { Platform } from './platforms';

export type Organizer = {
  id: number;
  userId: number;
  storeId: number;
  platformId: Nullable<number>;
  countryId: number;
  regionId: Nullable<number>;
  region: Nullable<RankingRegion>;
  name: string;
  description: Nullable<string>;
  slug: Nullable<string>;
  reportResults: Nullable<boolean>;
  googleCalendarConnected: Nullable<boolean>;
  googleCalendarId: Nullable<string>;
  limitlessOrganizerId: Nullable<number>;
  /**
   * Sólo lo traen el detalle y las mutations: en el listado sería una query por
   * organizador.
   */
  challongeUsernames?: string[];
  logoUrl: Nullable<string>;

  user: Nullable<User>;
  store: Nullable<Store>;
  platform: Nullable<Platform>;
  country: Country;
};

export const organizerDetailSchema = z.object({
  userId: z.number().optional(),
  storeId: z.number().optional(),
  platformId: z.number().optional(),
  countryId: z.number().optional(),
  regionId: z.number().optional(),
  name: z.string().min(1),
  slug: z.string().optional(),
  description: z.string().optional(),
  reportResults: z.boolean().optional(),
  limitlessOrganizerId: z.number().nullable().optional(),
  /** Usernames o links al perfil. Reemplaza la lista entera. */
  challongeUsernames: z.array(z.string()).optional(),
});

export const listOrganizers = async (
  user: Nullable<UserWithToken>,
  variables?: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<WithMeta<Organizer>>(
    gql`
      query organizers($filters: OrganizerFilters) {
        data: organizers(filters: $filters) {
          data {
            id
            name
            slug
            description
            reportResults
            googleCalendarConnected
            googleCalendarId
            limitlessOrganizerId
            logoUrl
            regionId

            region {
              id
              name
              slug
            }

            user {
              id
              name
              lastName
            }

            store {
              id
              name
              city
              province {
                id
                name
              }
            }

            platform {
              id
              name
              slug
            }
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    { filters: variables }
  );

export const listPublicOrganizers = async (
  user: Nullable<UserWithToken>,
  variables?: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<WithMeta<Organizer>>(
    gql`
      query tournamentOrganizers($filters: OrganizerFilters) {
        data: tournamentOrganizers(filters: $filters) {
          data {
            id
            name
            description
            slug
            logoUrl
            store {
              id
              name
            }
          }
        }
      }
    `,
    { filters: variables }
  );

export const getOrganizer = async (user: Nullable<UserWithToken>, id: number) =>
  getGraphQLQueryClient(user).request<WithData<Organizer>>(
    gql`
      query organizer($id: Int!) {
        data: organizer(id: $id) {
          id
          name
          slug
          description
          reportResults
          googleCalendarConnected
          googleCalendarId
          limitlessOrganizerId
          challongeUsernames
          logoUrl

          user {
            id
            name
          }

          store {
            id
            name
          }

          platform {
            id
            name
            slug
          }
        }
      }
    `,
    { id }
  );

/** El staff del organizador. Al que no lo administra el API le devuelve vacío. */
export const getOrganizerEmployees = async (
  user: Nullable<UserWithToken>,
  organizerId: number
) =>
  getGraphQLQueryClient(user).request<
    WithData<{ id: number; employees: OrganizerEmployee[] }>
  >(
    gql`
      query organizerEmployees($id: Int!) {
        data: organizer(id: $id) {
          id
          employees {
            id
            organizerId
            userId
            role
            user {
              id
              name
              secondName
              lastName
              secondLastName
              email
              cossyId
            }
          }
        }
      }
    `,
    { id: organizerId }
  );

/** Roles admitidos: ORGANIZER y OWNER. */
export const addOrganizerEmployee = async (
  user: Nullable<UserWithToken>,
  organizerId: number,
  userId: number,
  role: string
) =>
  getGraphQLQueryClient(user).request(
    gql`
      mutation addOrganizerEmployee(
        $organizerId: Int!
        $userId: Int!
        $role: UserRole!
      ) {
        data: addOrganizerEmployee(
          organizerId: $organizerId
          userId: $userId
          role: $role
        ) {
          id
        }
      }
    `,
    { organizerId, userId, role }
  );

export const updateOrganizerEmployee = async (
  user: Nullable<UserWithToken>,
  id: number,
  role: string
) =>
  getGraphQLQueryClient(user).request(
    gql`
      mutation updateOrganizerEmployee($id: Int!, $role: UserRole!) {
        data: updateOrganizerEmployee(id: $id, role: $role) {
          id
        }
      }
    `,
    { id, role }
  );

export const removeOrganizerEmployee = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request(
    gql`
      mutation removeOrganizerEmployee($id: Int!) {
        data: removeOrganizerEmployee(id: $id) {
          id
        }
      }
    `,
    { id }
  );

export const getOrganizerBySlug = async (slug: string) =>
  getGraphQLQueryClient(null).request<WithData<Organizer>>(
    gql`
      query organizerBySlug($slug: String!) {
        data: organizerBySlug(slug: $slug) {
          id
          name
          slug
          logoUrl
          store {
            id
            name
            address
            city
            logoUrl
            googleMapsUrl
            links {
              id
              type
              url
              label
            }
            employees {
              id
              role
              user {
                id
                name
                lastName
                picture
              }
            }
          }
        }
      }
    `,
    { slug }
  );

export const createOrganizer = async (
  user: Nullable<UserWithToken>,
  input: z.infer<typeof organizerDetailSchema>
) =>
  getGraphQLQueryClient(user).request<WithData<Organizer>>(
    gql`
      mutation createOrganizer($input: OrganizerInput!) {
        data: createOrganizer(input: $input) {
          id
          name
          description
          reportResults
          limitlessOrganizerId
          challongeUsernames
          logoUrl

          user {
            id
            name
          }

          store {
            id
            name
          }

          platform {
            id
            name
            slug
          }
        }
      }
    `,
    { input }
  );

export const updateOrganizer = async (
  user: Nullable<UserWithToken>,
  id: number,
  input: z.infer<typeof organizerDetailSchema>
) =>
  getGraphQLQueryClient(user).request<WithData<Organizer>>(
    gql`
      mutation updateOrganizer($id: Int!, $input: OrganizerInput!) {
        data: updateOrganizer(id: $id, input: $input) {
          id
          name
          description
          reportResults
          limitlessOrganizerId
          challongeUsernames
          logoUrl
          regionId
          region {
            id
            name
          }

          user {
            id
            name
          }

          store {
            id
            name
          }

          platform {
            id
            name
            slug
          }
        }
      }
    `,
    { id, input }
  );

export const uploadOrganizerLogo = (
  organizerId: number,
  file: File,
  config?: AxiosRequestConfig
) => {
  const formData = new FormData();
  formData.append('logo', file);

  return axiosClient.post(`/organizers/${organizerId}/logo`, formData, {
    ...config,
    headers: {
      ...config?.headers,
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const deleteOrganizerLogo = (
  organizerId: number,
  config?: AxiosRequestConfig
) => {
  return axiosClient.delete(`/organizers/${organizerId}/logo`, config);
};

export const connectGoogleCalendar = async (
  user: Nullable<UserWithToken>,
  organizerId: number
) =>
  getGraphQLQueryClient(user).request<WithData<string>>(
    gql`
      mutation connectGoogleCalendar($organizerId: Int!) {
        data: connectGoogleCalendar(organizerId: $organizerId)
      }
    `,
    { organizerId }
  );

export const disconnectGoogleCalendar = async (
  user: Nullable<UserWithToken>,
  organizerId: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation disconnectGoogleCalendar($organizerId: Int!) {
        data: disconnectGoogleCalendar(organizerId: $organizerId)
      }
    `,
    { organizerId }
  );

export type GoogleCalendar = {
  id: string;
  summary: string;
  description?: string;
  primary?: boolean;
  accessRole: string;
};

export const listGoogleCalendars = (config?: AxiosRequestConfig) => {
  return axiosClient.get<GoogleCalendar[]>(
    `/google-calendar/calendars`,
    config
  );
};

export const selectGoogleCalendar = (
  organizerId: number,
  calendarId: string,
  config?: AxiosRequestConfig
) => {
  return axiosClient.put(
    `/google-calendar/calendar/${organizerId}`,
    { calendarId },
    config
  );
};

export const createGoogleCalendar = async (
  user: Nullable<UserWithToken>,
  organizerId: number,
  calendarName: string
) =>
  getGraphQLQueryClient(user).request<WithData<string>>(
    gql`
      mutation createGoogleCalendar($organizerId: Int!, $calendarName: String!) {
        data: createGoogleCalendar(organizerId: $organizerId, calendarName: $calendarName)
      }
    `,
    { organizerId, calendarName }
  );

export const getGoogleCalendarAuthUrl = (config?: AxiosRequestConfig) => {
  return axiosClient.get<{ authUrl: string }>('/google-calendar/authorize', config);
};

export const disconnectGlobalGoogleCalendar = (config?: AxiosRequestConfig) => {
  return axiosClient.delete('/google-calendar/disconnect', config);
};

export const isGoogleCalendarConnected = (config?: AxiosRequestConfig) => {
  return axiosClient.get<{ connected: boolean }>('/google-calendar/status', config);
};
